import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  UserPlus, 
  BookOpen, 
  GraduationCap, 
  CreditCard, 
  Users, 
  BarChart3, 
  Shield, 
  Database,
  Calendar,
  QrCode,
  Settings,
  CheckCircle,
  ChevronRight,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';
import { useState } from 'react';

export default function LandingPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    schoolType: '',
    size: '',
    comments: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <div className="flex items-center">
              <img src="/qcampusone-logo.png" alt="QCampusOne" className="h-16 w-auto object-contain" />
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/login">
                <Button variant="ghost" className="text-gray-600 hover:text-gray-900 font-medium">
                  Iniciar Sesión
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="bg-[#2F80FF] hover:bg-[#1E5EFF] text-white font-medium shadow-sm">
                  Comenzar Ahora
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative overflow-hidden">
        <div className="container mx-auto px-4 pt-16 pb-24">
          <div className="text-center max-w-6xl mx-auto relative z-10">
            <div className="inline-flex items-center px-4 py-2 bg-[#E6F0FF] text-[#2F80FF] rounded-full text-sm font-medium mb-8">
              <div className="flex items-center space-x-2">
                <div className="flex items-end space-x-0.5 h-4 mb-0.5">
                  <div className="w-1 bg-[#2F80FF] h-1/3 rounded-t-sm"></div>
                  <div className="w-1 bg-[#2F80FF] h-1/2 rounded-t-sm"></div>
                  <div className="w-1 bg-[#2F80FF] h-3/4 rounded-t-sm"></div>
                  <div className="w-1 bg-[#2F80FF] h-full rounded-t-sm"></div>
                </div>
                <span>Cada vez mas Instituciones educativas confian en nosotros</span>
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#111827] mb-8 leading-tight">
              QCampusOne: el 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2F80FF] to-[#4F46E5]"> sistema operativo</span> de tu escuela
            </h1>
            <p className="text-xl md:text-2xl text-[#4B5563] mb-12 max-w-4xl mx-auto leading-relaxed">
              Centraliza alumnos, control escolar, finanzas y docencia con tableros en tiempo real.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
              <Link to="/signup">
                <Button size="lg" className="px-8 py-4 text-lg bg-[#2F80FF] hover:bg-[#1E5EFF] text-white font-semibold shadow-lg hover:shadow-xl transition-all rounded-lg">
                  Comenzar Ahora <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Button variant="outline" size="lg" className="px-8 py-4 text-lg border-[#9CA3AF] hover:border-[#2F80FF] hover:text-[#2F80FF] font-semibold transition-all rounded-lg">
                Descargar Folleto
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-sm text-[#4B5563] max-w-2xl mx-auto">
              <div className="flex items-center justify-center space-x-2">
                <CheckCircle className="w-5 h-5 text-[#2F80FF]" />
                <span>Instalación en 24 horas</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <CheckCircle className="w-5 h-5 text-[#2F80FF]" />
                <span>Integración bancaria automática</span>
              </div>
              <div className="flex items-center justify-center space-x-2">
                <CheckCircle className="w-5 h-5 text-[#2F80FF]" />
                <span>Soporte técnico dedicado</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Beneficios Clave */}
      <section className="bg-[#F3F4F6] py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#111827] mb-6">
              ¿Por qué elegir QCampusOne?
            </h2>
            <p className="text-xl text-[#4B5563] max-w-3xl mx-auto">
              Optimiza cada aspecto de la gestión escolar con tecnología de vanguardia
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <BarChart3 className="w-10 h-10 text-[#2F80FF]" />,
                title: "Eficiencia operativa 360°",
                description: "Reduce hasta 40% el tiempo operativo al eliminar Excel y WhatsApp dispersos."
              },
              {
                icon: <Database className="w-10 h-10 text-[#2F80FF]" />,
                title: "Decisiones con datos en tiempo real",
                description: "Tableros ejecutivos con KPIs actualizados cada minuto para decisiones informadas."
              },
              {
                icon: <CreditCard className="w-10 h-10 text-[#2F80FF]" />,
                title: "Cobranza inteligente y conciliación automática",
                description: "Cobranza inteligente con reglas de gracia/recargo y conciliación en minutos."
              },
              {
                icon: <Users className="w-10 h-10 text-[#2F80FF]" />,
                title: "Mejor experiencia para alumnos y docentes",
                description: "Semáforos de riesgo académico y alertas tempranas para intervención oportuna."
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                <div className="mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-[#111827] mb-4">
                  {benefit.title}
                </h3>
                <p className="text-[#4B5563]">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Módulos/Prestaciones */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#111827] mb-6">
              Módulos Integrados
            </h2>
            <p className="text-xl text-[#4B5563] max-w-3xl mx-auto">
              Toda la funcionalidad que necesitas en un solo sistema
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <UserPlus className="w-8 h-8 text-[#2F80FF]" />,
                title: "Admisiones e Inscripciones",
                description: "Kommo, checklist, matrícula automática"
              },
              {
                icon: <BookOpen className="w-8 h-8 text-[#2F80FF]" />,
                title: "Panel del Alumno",
                description: "Kardex, horarios, materiales, pagos/facturas"
              },
              {
                icon: <GraduationCap className="w-8 h-8 text-[#2F80FF]" />,
                title: "Control Escolar",
                description: "Planificador sin colisiones, asistencia QR, calificaciones"
              },
              {
                icon: <CreditCard className="w-8 h-8 text-[#2F80FF]" />,
                title: "Finanzas",
                description: "Validación de pagos, conciliación, CFDI 4.0"
              },
              {
                icon: <Calendar className="w-8 h-8 text-[#2F80FF]" />,
                title: "Docencia (PWA)",
                description: "Agenda, pase de lista QR, rúbricas, sustituciones"
              },
              {
                icon: <BarChart3 className="w-8 h-8 text-[#2F80FF]" />,
                title: "Dirección",
                description: "KPIs: cortes, morosidad, rezago, ocupación"
              },
              {
                icon: <QrCode className="w-8 h-8 text-[#2F80FF]" />,
                title: "Operación Interna",
                description: "Tickets, control de acceso QR, inventarios"
              },
              {
                icon: <Shield className="w-8 h-8 text-[#2F80FF]" />,
                title: "Seguridad/IAM",
                description: "RBAC/ABAC, 2FA, SSO/OIDC, auditoría"
              }
            ].map((module, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-[#E5E7EB] hover:border-[#2F80FF] hover:shadow-md transition-all group">
                <div className="mb-4">
                  {module.icon}
                </div>
                <h3 className="text-lg font-bold text-[#111827] mb-3">
                  {module.title}
                </h3>
                <p className="text-sm text-[#4B5563] group-hover:text-[#2F80FF] transition-colors">
                  {module.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cómo Funciona */}
      <section className="bg-[#F3F4F6] py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#111827] mb-6">
              Cómo Funciona
            </h2>
            <p className="text-xl text-[#4B5563]">
              Implementación simple en 3 pasos
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Conecta tus datos",
                description: "Integramos con Kommo, sistemas bancarios, PAC y tus herramientas actuales",
                icon: <Database className="w-12 h-12 text-[#2F80FF]" />
              },
              {
                step: "02", 
                title: "Configura reglas",
                description: "Establece períodos de gracia, recargos, horarios y permisos según tu institución",
                icon: <Settings className="w-12 h-12 text-[#2F80FF]" />
              },
              {
                step: "03",
                title: "Opera y analiza",
                description: "Utiliza tableros en tiempo real y automatizaciones para optimizar tu gestión",
                icon: <BarChart3 className="w-12 h-12 text-[#2F80FF]" />
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="bg-white p-8 rounded-xl shadow-sm mb-6">
                  <div className="text-4xl font-bold text-[#E6F0FF] mb-4">{step.step}</div>
                  <div className="mb-6">{step.icon}</div>
                  <h3 className="text-2xl font-bold text-[#111827] mb-4">{step.title}</h3>
                  <p className="text-[#4B5563]">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimoniales/Prueba Social */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#111827] mb-6">
              Confían en QCampusOne
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-white rounded-xl border border-[#E5E7EB]">
              <div className="text-4xl font-bold text-[#2F80FF] mb-2">Cada vez +</div>
              <div className="text-[#4B5563]">Instituciones educativas confían en nosotros</div>
            </div>
            <div className="text-center p-8 bg-white rounded-xl border border-[#E5E7EB]">
              <div className="text-4xl font-bold text-[#2F80FF] mb-2">95%</div>
              <div className="text-[#4B5563]">Satisfacción del cliente</div>
            </div>
            <div className="text-center p-8 bg-white rounded-xl border border-[#E5E7EB]">
              <div className="text-4xl font-bold text-[#2F80FF] mb-2">40%</div>
              <div className="text-[#4B5563]">Reducción en tiempo operativo</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F3F4F6] py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#111827] mb-6">
              Preguntas Frecuentes
            </h2>
          </div>
          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                q: "¿Cuánto tiempo toma la implementación?",
                a: "La implementación completa toma entre 2-4 semanas, dependiendo del tamaño de la institución y los sistemas existentes."
              },
              {
                q: "¿Se integra con nuestro sistema bancario actual?",
                a: "Sí, QCampusOne se integra con los principales bancos mexicanos para conciliación automática de pagos."
              },
              {
                q: "¿Qué tipo de capacitación incluyen?",
                a: "Incluimos capacitación completa para administradores y usuarios finales, plus documentación y videos tutoriales."
              },
              {
                q: "¿Los datos están seguros?",
                a: "Absolutamente. Utilizamos cifrado de grado militar, hosting en Azure México y cumplimos con LFPDPPP."
              },
              {
                q: "¿Puedo acceder desde dispositivos móviles?",
                a: "Sí, QCampusOne incluye aplicaciones móviles nativas y PWA para acceso desde cualquier dispositivo."
              },
              {
                q: "¿Qué pasa si necesito funcionalidades específicas?",
                a: "Ofrecemos módulos personalizables y API para integraciones específicas según las necesidades de tu institución."
              },
              {
                q: "¿Incluye soporte técnico?",
                a: "Sí, incluimos soporte técnico 24/7 por chat, teléfono y WhatsApp, plus un gerente de cuenta dedicado."
              },
              {
                q: "¿Cómo funciona el pricing?",
                a: "El precio se basa en el número de alumnos activos. Contáctanos para una cotización personalizada con descuentos por volumen."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-lg font-bold text-[#111827] mb-3">{faq.q}</h3>
                <p className="text-[#4B5563]">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final con Formulario */}
      <section className="py-24 bg-gradient-to-r from-[#2F80FF] to-[#4F46E5]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Comienza tu transformación digital hoy
              </h2>
              <p className="text-xl text-blue-100">
                Solicita una demo personalizada y descubre cómo QCampusOne puede revolucionar tu institución
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#111827] mb-2">Nombre completo</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#9CA3AF] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F80FF] focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#111827] mb-2">Email corporativo</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#9CA3AF] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F80FF] focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#111827] mb-2">Teléfono</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#9CA3AF] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F80FF] focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#111827] mb-2">Tipo de escuela</label>
                    <select
                      name="schoolType"
                      value={formData.schoolType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#9CA3AF] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F80FF] focus:border-transparent"
                      required
                    >
                      <option value="">Seleccionar tipo</option>
                      <option value="preescolar">Preescolar</option>
                      <option value="primaria">Primaria</option>
                      <option value="secundaria">Secundaria</option>
                      <option value="preparatoria">Preparatoria</option>
                      <option value="universidad">Universidad</option>
                      <option value="instituto">Instituto Técnico</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#111827] mb-2">Tamaño (número de alumnos)</label>
                    <select
                      name="size"
                      value={formData.size}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-[#9CA3AF] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F80FF] focus:border-transparent"
                      required
                    >
                      <option value="">Seleccionar tamaño</option>
                      <option value="1-100">1-100 alumnos</option>
                      <option value="101-500">101-500 alumnos</option>
                      <option value="501-1000">501-1,000 alumnos</option>
                      <option value="1000+">Más de 1,000 alumnos</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-[#111827] mb-2">Comentarios o necesidades específicas</label>
                    <textarea
                      name="comments"
                      value={formData.comments}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-[#9CA3AF] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2F80FF] focus:border-transparent"
                    ></textarea>
                  </div>
                </div>
                <div className="text-center">
                  <Button
                    type="submit"
                    size="lg"
                    className="px-8 py-4 text-lg bg-[#2F80FF] hover:bg-[#1E5EFF] text-white font-semibold shadow-lg hover:shadow-xl transition-all rounded-lg"
                  >
                    Comenzar Ahora <ChevronRight className="ml-2 w-5 h-5" />
                  </Button>
                  <p className="text-sm text-[#4B5563] mt-4">
                    Te contactaremos en las próximas 2 horas para programar tu demo personalizada
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#111827] py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <img src="/qcampusone-logo.png" alt="QCampusOne" className="h-16 w-auto object-contain mb-6 brightness-0 invert" />
              <p className="text-gray-400 mb-6">
                El sistema operativo de tu escuela. Centraliza y optimiza toda tu gestión educativa.
              </p>
            </div>
            <div>
              <h3 className="text-white font-bold mb-4">Producto</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Módulos</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Configuración General</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Seguridad</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold mb-4">Soporte</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Centro de Ayuda</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Documentación</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Capacitación</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Estado del Sistema</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold mb-4">Contacto</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>+52 (55) 1234-5678</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>hola@qcampusone.com</span>
                </li>
                <li className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>Ciudad de México, México</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                © 2025 QCampusOne. Todos los derechos reservados.
              </p>
              <div className="flex space-x-6 text-sm text-gray-400 mt-4 md:mt-0">
                <a href="#" className="hover:text-white transition-colors">Términos de Servicio</a>
                <a href="#" className="hover:text-white transition-colors">Política de Privacidad</a>
                <a href="#" className="hover:text-white transition-colors">Aviso de Privacidad</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}